import React, { useEffect, useRef, useState } from "react";

import { Grid, Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import queryString from "query-string";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../components/PublicFooter";
import AuthLeftPanel from "../../../components/AuthLeftPanel";
import {
  USER_PERMISSIONS,
  USER_TOKEN,
  CURRENT_USER,
  USER_UID,
  USER_COMPANY,
} from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { changeTitlePageByResource } from "../../../helpers/helpers";
import useCurrentBrand from "../../../hooks/useCurrentBrand";
import { Error } from "../../../models/error";
import { User } from "../../../models/user";
import TranslateService from "../../../services/translate.service";
import UserService from "../../../services/user.service";
import { MfaLoginResponse } from "../../../services/mfa.service";
import { getBrandByCompanyName } from "../../../util/brands";
import { CodeError } from "../../../util/enums";
import SigninForm, { SigninFormHandler } from "./components/Form";
import MfaVerification from "./components/MfaVerification";
import useStyles from "./styles";

const SignIn = () => {
  const classes = useStyles();

  const { t: translate } = useTranslation();

  const parsed: { continue?: string } = queryString.parse(location.search);

  const { setCurrentBrand } = useCurrentBrand();

  const { setActiveLanguageId, currentUser, userLogout } = useAppState();

  const signinFormComponentRef = useRef<SigninFormHandler>(null);

  useEffect(() => {
    if (currentUser && currentUser.id) {
      userLogout();
    }
  }, []);

  const [responseData, setResponseData] = useState<{
    token: string;
    user: User;
  }>();

  const [errorReturned, setErrorReturned] = useState<Error>();

  const [urlToRedirect, setUrlToRedirect] = useState<string>("");

  const [showMfaScreen, setShowMfaScreen] = useState<boolean>(false);
  const [mfaData, setMfaData] = useState<MfaLoginResponse | null>(null);
  const [loginPayload, setLoginPayload] = useState<{ email: string; password: string } | null>(
    null
  );

  useQuery(["permissions.get-by-current-user"], () => UserService.getUserPermissions(), {
    enabled: !!localStorage.getItem(USER_TOKEN),
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response?.data) {
        localStorage.setItem(USER_PERMISSIONS, JSON.stringify(response?.data));
      }
    },
  });

  const { mutate: validateUser, isLoading: isLoadingValidate } = useMutation(
    ["users.login"],
    (login: { email: string; password: string }) => UserService.login(login.email, login.password),
    {
      onSuccess: (response) => {
        if (response && response?.data?.success) {
          // Check if MFA is required
          if (response.data.requiresMfa) {
            setMfaData(response.data as MfaLoginResponse);
            setShowMfaScreen(true);
            setErrorReturned(undefined);
          } else {
            // Normal login without MFA
            onSuccess(response);
            setErrorReturned(undefined);
          }
        }
      },
      onError: (err: AxiosError<Error>) => {
        if (err.response?.data?.error === CodeError.FORBIDDEN) {
          NotificationToast.error(err.response.data.message);
        } else {
          const errorReturned = err?.response?.data ? (err.response?.data as Error) : undefined;

          NotificationToast.error(err?.response?.data?.message || translate("anErrorOccurred"));

          setErrorReturned(errorReturned);
        }
      },
    }
  );

  const { isFetching: isFetchingTranslate } = useQuery(
    ["translate.getAll"],
    () =>
      TranslateService.getAll({
        description: "English (Default)",
        companyId: responseData?.user?.companyId,
        status: "Active",
        type: "Web",
      }),
    {
      enabled: !!responseData,
      onSuccess: (response) => {
        if (response && response.data && response?.data?.length > 0) {
          login(response.data[0].id);
        } else {
          login();
        }
      },
      onError: () => login(),
    }
  );

  useEffect(() => {
    changeTitlePageByResource("signin");
  }, []);

  useEffect(() => {
    if (parsed?.continue) {
      setUrlToRedirect(parsed.continue);
    }
  }, [parsed]);

  const onSuccess = (response: AxiosResponse<any>) => {
    if (response && !response?.data) {
      NotificationToast.error(translate("thePasswordDoesNotMatch"));

      return;
    }

    localStorage.setItem(USER_TOKEN, response.data.token);

    setResponseData(response.data);
  };

  const login = (activeLanguageId?: number) => {
    if (responseData) {
      NotificationToast.success(translate("userLoggedIn"));

      const isBrandingEnabled = process.env.IS_BRANDING_ENABLED === "true";

      const { id } = responseData.user.company;

      localStorage.setItem(USER_UID, responseData.token);
      localStorage.setItem(USER_COMPANY, JSON.stringify(responseData.user.company));
      localStorage.setItem(CURRENT_USER, JSON.stringify(responseData.user));

      const token = responseData.token;
      const cookieExpirationDays = 1;
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + cookieExpirationDays * 24 * 60 * 60 * 1000);

      document.cookie = `authToken=${token}; domain=.shipeezi.com; path=/; secure; samesite=lax; expires=${expirationDate.toUTCString()}`;

      if (isBrandingEnabled) setCurrentBrand(getBrandByCompanyName(id!));

      if (Number(activeLanguageId) || Number(responseData?.user?.translateId))
        setActiveLanguageId(Number(responseData?.user?.translateId) || Number(activeLanguageId));

      if (urlToRedirect) {
        // If redirecting to amplifyapp.com domain, add token to URL for SSO
        try {
          const redirectUrl = new URL(urlToRedirect);
          if (redirectUrl.hostname.includes("amplifyapp.com")) {
            redirectUrl.searchParams.set("token", token);
            window.location.replace(redirectUrl.toString());
          } else {
            window.location.replace(urlToRedirect);
          }
        } catch {
          // If URL parsing fails, redirect as-is
          window.location.replace(urlToRedirect);
        }

        return;
      }

      window.location.replace("/");

      Sentry.setUser({
        username: responseData?.user?.username,
        fullName:
          responseData?.user?.fullName ??
          `${responseData?.user?.name}${responseData?.user?.lastName}`,
      });
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const email = signinFormComponentRef?.current?.email ?? "";
    const password = signinFormComponentRef?.current?.password ?? "";

    setLoginPayload({ email, password });
    validateUser({
      email,
      password,
    });
  };

  const handleMfaSuccess = (token: string, user: User) => {
    // After MFA validation, proceed with login
    onSuccess({
      data: {
        success: true,
        token,
        user,
      },
    } as any);
  };

  const handleMfaBack = () => {
    setShowMfaScreen(false);
    setMfaData(null);
    setLoginPayload(null);
  };

  const [rightSideLoaded, setRightSideLoaded] = useState(false);

  useEffect(() => {
    // Left side loads first, then right side after delay
    const timer = setTimeout(() => {
      setRightSideLoaded(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={classes.mainDiv}>
      <Grid className={classes.container} container direction="row">
        <Grid item xs={12} sm={6}>
          <Box className={classes.leftBox}>
            <AuthLeftPanel showContentDelay={0} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            className={classes.rightBox}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              opacity: rightSideLoaded ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "520px",
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                {showMfaScreen && mfaData ? (
                  <MfaVerification
                    mfaSessionId={mfaData.mfaSessionId}
                    availableMethods={mfaData.availableMethods}
                    deliveries={mfaData.deliveries}
                    expiresInSeconds={mfaData.expiresInSeconds}
                    onSuccess={handleMfaSuccess}
                    onBack={handleMfaBack}
                    purpose="LOGIN"
                    loginPayload={loginPayload ?? undefined}
                  />
                ) : (
                  <SigninForm
                    ref={signinFormComponentRef}
                    isLoading={isFetchingTranslate || isLoadingValidate}
                    errorReturned={errorReturned}
                    handleSubmit={handleSubmit}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                width: "100%",
                maxWidth: "520px",
                pb: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <PublicFooter />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

export default SignIn;
